import { IValidationItem, IValidationSeverity } from '@stair-craft/common/util';

import { IStairNorms, RESIDENTIAL_NORMS } from './norms';

/** Warning band of a two-sided range: a tenth of the range width, inside each edge. */
const TWO_SIDED_WARNING_BAND_FRACTION: number = 0.1;

/** Warning band of a one-sided limit: a twentieth of the limit, on the allowed side. */
export const ONE_SIDED_WARNING_BAND_FRACTION: number = 0.05;

function unitMm(): string {
    return $localize`:@@unitMm:мм`;
}

function unitDeg(): string {
    return $localize`:@@unitDeg:°`;
}

function checkRange(code: string, value: number, min: number, max: number, label: string, unit: string = unitMm()): IValidationItem {
    let severity: IValidationSeverity = 'ok';
    if (value < min || value > max) {
        severity = 'error';
    } else {
        const buffer: number = (max - min) * TWO_SIDED_WARNING_BAND_FRACTION;
        if (value < min + buffer || value > max - buffer) {
            severity = 'warning';
        }
    }

    return {
        code,
        severity,
        value,
        min,
        max,
        message: `${label}: ${String(Math.round(value))}${unit} (${String(min)}–${String(max)})`,
    };
}

function checkMin(code: string, value: number, min: number, label: string, unit: string = unitMm()): IValidationItem {
    let severity: IValidationSeverity = 'ok';
    if (value < min) {
        severity = 'error';
    } else if (value < min + min * ONE_SIDED_WARNING_BAND_FRACTION) {
        severity = 'warning';
    } else {
        /* no-op: значение в норме, severity остаётся 'ok' */
    }

    const minLabel: string = $localize`:@@unitMin:мин`;
    return {
        code,
        severity,
        value,
        min,
        max: null,
        message: `${label}: ${String(Math.round(value))}${unit} (${minLabel} ${String(min)})`,
    };
}

function checkMax(code: string, value: number, max: number, label: string, unit: string = unitMm()): IValidationItem {
    let severity: IValidationSeverity = 'ok';
    if (value > max) {
        severity = 'error';
    } else if (value > max - max * ONE_SIDED_WARNING_BAND_FRACTION) {
        severity = 'warning';
    } else {
        /* no-op: значение в норме, severity остаётся 'ok' */
    }

    const maxLabel: string = $localize`:@@unitMax:макс`;
    return {
        code,
        severity,
        value,
        max,
        min: null,
        message: `${label}: ${String(Math.round(value))}${unit} (${maxLabel} ${String(max)})`,
    };
}

/**
 * A headroom error names what to change. The free parameters are already picked to keep the stair
 * in norm, so what is left is the user's: a straight, L or U stair under the slab needs a larger
 * opening, and a spiral that climbs too little per turn needs a wider diameter.
 */
export function withHeadroomHint(items: IValidationItem[], spiral: boolean): IValidationItem[] {
    const hint: string = spiral
        ? $localize`:@@valHeadroomHintSpiral:увеличьте диаметр лестницы`
        : $localize`:@@valHeadroomHintOpening:увеличьте проём`;
    return items.map((item: IValidationItem) =>
        item.code === 'HEADROOM' && item.severity === 'error' ? { ...item, message: `${item.message} — ${hint}` } : item
    );
}

export function validate(
    riserHeight: number,
    treadDepth: number,
    angle: number,
    headroom: number,
    stairWidth: number,
    norms: IStairNorms = RESIDENTIAL_NORMS
): IValidationItem[] {
    const items: IValidationItem[] = [];

    items.push(
        checkRange('RISER_HEIGHT', riserHeight, norms.riserHeightMin, norms.riserHeightMax, $localize`:@@valRiserHeight:Высота подступенка`)
    );

    items.push(
        checkRange('TREAD_DEPTH', treadDepth, norms.treadDepthMin, norms.treadDepthMax, $localize`:@@valTreadDepth:Глубина проступи`)
    );

    const blondel: number = 2 * riserHeight + treadDepth;
    items.push(checkRange('BLONDEL', blondel, norms.blondelMin, norms.blondelMax, $localize`:@@valBlondel:Формула Блонделя (2h+g)`));

    items.push(checkRange('ANGLE', angle, norms.angleMin, norms.angleMax, $localize`:@@valAngle:Угол наклона`, unitDeg()));

    items.push(checkMin('HEADROOM', headroom, norms.headroomMin, $localize`:@@valHeadroom:Просвет (высота прохода)`));

    items.push(checkMin('STAIR_WIDTH', stairWidth, norms.stairWidthMin, $localize`:@@valStairWidth:Ширина марша`));

    return items;
}

export function validateRailing(
    railing: { enabled: boolean; height: number; balusterSpacing: number },
    norms: IStairNorms = RESIDENTIAL_NORMS
): IValidationItem[] {
    if (!railing.enabled) {
        return [];
    }

    return [
        checkMin('RAILING_HEIGHT', railing.height, norms.railingHeightMin, $localize`:@@valRailingHeight:Высота ограждения`),
        checkMax('BALUSTER_SPACING', railing.balusterSpacing, norms.balusterSpacingMax, $localize`:@@valBalusterSpacing:Шаг балясин`),
    ];
}
